/**
 * PreviewSummary — top-of-panel summary of the live preview result.
 */

import { FC, memo, useMemo } from 'react';
import type {
  FlowLayer,
  FlowOptimizerPreviewResult,
} from '@trafficpulse/shared';

import {
  summarizePreviewItems,
  type PreviewSummaryBuckets,
} from '../../utils/preview-summary.util';

export interface PreviewSummaryProps {
  result: FlowOptimizerPreviewResult;
  applied: boolean;
}

const LAYERS: ReadonlyArray<{ id: FlowLayer; label: string }> = [
  { id: 'paths', label: 'Paths' },
  { id: 'landers', label: 'Landers' },
  { id: 'offers', label: 'Offers' },
];

const PreviewSummary: FC<PreviewSummaryProps> = ({ result, applied }) => {
  const buckets = useMemo(
    () => summarizePreviewItems(result.items),
    [result.items],
  );
  const adjusted = result.totals.weightRuleAdjustedGroups ?? 0;
  return (
    <div className="space-y-1.5 rounded-md border border-border bg-muted/10 p-2 text-xs">
      <SummaryLine buckets={buckets} applied={applied} />
      <LayerDeltaRow buckets={buckets} />
      {adjusted > 0 ? (
        <p className="rounded border border-warning/40 bg-warning/10 px-1.5 py-1 text-[11px] leading-snug text-muted-foreground">
          <span className="font-semibold text-warning">Orange weights</span>{' '}
          were auto-adjusted to fit 100% — hover one to see what its rule asked
          for and what fit.
        </p>
      ) : null}
    </div>
  );
};

const SummaryLine: FC<{ buckets: PreviewSummaryBuckets; applied: boolean }> = ({
  buckets,
  applied,
}) => (
  <p>
    <strong className="text-foreground">{buckets.changed}</strong> changed ·{' '}
    <strong className="text-foreground">{buckets.unchanged}</strong> unchanged ·{' '}
    <strong className="text-foreground">{buckets.passThrough}</strong> kept
    {applied ? <span className="ml-1 text-emerald-600">· applied</span> : null}
  </p>
);

const LayerDeltaRow: FC<{ buckets: PreviewSummaryBuckets }> = ({ buckets }) => (
  <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
    {LAYERS.map((l) => (
      <span key={l.id} className="tabular-nums">
        <strong className="text-foreground">{l.label}</strong>{' '}
        {(buckets.deltaPerLayer[l.id] / 10).toFixed(1)}%
      </span>
    ))}
  </div>
);

export default memo(PreviewSummary);
